import type {
    IShikshaClientTypeRevenue,
    IShikshaDealsAtRiskItem,
    IShikshaKpiItem,
    IShikshaSalesFunnelStage,
    IShikshaTeamContributionMember,
} from '@features/home/models/shiksha-dashboard.model'

export const SHIKSHA_DASHBOARD_SUBTITLE_HEADER_ICON_SIZES = ['xs', 'sm', 'md', 'lg', 'xl'] as const

export type ShikshaDashboardSubtitleHeaderIconSize =
    (typeof SHIKSHA_DASHBOARD_SUBTITLE_HEADER_ICON_SIZES)[number]

export const SHIKSHA_DASHBOARD_SUBTITLE_HEADER_UI = {
    defaultIconSize: 'md' as ShikshaDashboardSubtitleHeaderIconSize,
    infoBadgeChar: 'i',
} as const

/** Label for team-wide Shiksha dashboard scope (shown only when the user has reportees). */
export const SHIKSHA_SCOPE_ENTIRE_TEAM = 'Entire Team' as const

/** UI labels and API scope tokens for Shiksha dashboard "view scope". */
export const SHIKSHA_DASHBOARD_SCOPE = {
    /** Personal scope label in the View Scope control (API still uses {@link SHIKSHA_DASHBOARD_SCOPE.MY}). */
    MYSELF: 'My Contribution',
    MY: 'my',
    TEAM: 'team',
} as const

export const SHIKSHA_SCOPE_OPTIONS: readonly string[] = [
    SHIKSHA_SCOPE_ENTIRE_TEAM,
    SHIKSHA_DASHBOARD_SCOPE.MYSELF,
]

/**
 * Internal scope control value for a single reportee (shown after base options when the user has a team).
 * Display name comes from the dropdown option label; API uses {@link parseShikshaReporteeScopeValue}.
 */
export const SHIKSHA_SCOPE_REPORTEE_VALUE_PREFIX = 'reportee:' as const

export function buildShikshaReporteeScopeValue(userId: string): string {
    const trimmed = userId?.trim() ?? ''
    return trimmed.length > 0 ? `${SHIKSHA_SCOPE_REPORTEE_VALUE_PREFIX}${trimmed}` : ''
}

export function parseShikshaReporteeScopeValue(scopeValue: string): string | null {
    if (!scopeValue?.startsWith(SHIKSHA_SCOPE_REPORTEE_VALUE_PREFIX)) {
        return null
    }
    const id = scopeValue.slice(SHIKSHA_SCOPE_REPORTEE_VALUE_PREFIX.length).trim()
    return id.length > 0 ? id : null
}

export const SHIKSHA_YEAR_OPTIONS: readonly string[] = ['2025-26', '2024-25', '2023-24']

/** April (1-indexed). Matches CRM `FINANCIAL_YEAR_CONSTANTS.MONTH_THRESHOLD` (Indian FY April–March). */
const SHIKSHA_FINANCIAL_YEAR_START_MONTH = 4

/** Label e.g. `2026-27` for the financial year containing `referenceDate`. */
export function getCurrentShikshaFinancialYearLabel(referenceDate = new Date()): string {
    const calendarYear = referenceDate.getFullYear()
    const month = referenceDate.getMonth() + 1
    const startYear = month >= SHIKSHA_FINANCIAL_YEAR_START_MONTH ? calendarYear : calendarYear - 1
    const endTwoDigits = (startYear + 1) % 100
    return `${startYear}-${String(endTwoDigits).padStart(2, '0')}`
}

/** Fallback year in the header before the API returns financial years. */
export function getInitialShikshaYearPlaceholder(referenceDate = new Date()): string {
    const currentLabel = getCurrentShikshaFinancialYearLabel(referenceDate)
    return SHIKSHA_YEAR_OPTIONS.includes(currentLabel) ? currentLabel : SHIKSHA_YEAR_OPTIONS[0]
}

export const SHIKSHA_KPI_ITEMS: readonly IShikshaKpiItem[] = []

export const SHIKSHA_TEAM_CONTRIBUTION_MEMBERS: readonly IShikshaTeamContributionMember[] = []

export const SHIKSHA_SALES_FUNNEL_STAGES: readonly IShikshaSalesFunnelStage[] = []

export const SHIKSHA_CLIENT_TYPE_REVENUES: readonly IShikshaClientTypeRevenue[] = []

export const SHIKSHA_DEALS_AT_RISK_ITEMS: readonly IShikshaDealsAtRiskItem[] = []

export const SHIKSHA_KPI_STRIP_CONTENT = {
    ariaLabel: 'Shiksha key performance indicators',
} as const

export const SHIKSHA_TEAM_CONTRIBUTION_CONTENT = {
    ariaLabel: 'Team contribution',
    title: 'Contribution Ranking',
    subtitle: 'Team members ranked based on their total closed won revenue.',
    teamRevenueSuffix: 'of team revenue',
    fallbackInitials: '--',
    rankHeader: 'Rank',
    teamMemberHeader: 'Team Member',
    pipelineHeader: 'Pipeline',
    dealWonHeader: 'Deal won',
    contributionHeader: 'Contribution',
    pipelineInfo: 'Total active pipeline value from deals currently in S2 and S3.',
    dealWonInfo: 'Total order value won (S5) by the selected team member.',
    contributionInfo: 'The percentage contribution of the team member to the overall team revenue.',
    emptyTitle: 'No contribution data available',
    emptyMessage: 'There are no members to show for the selected filters.',
    percentSymbol: '%',
    unavailableCellValue: '--',
    pipelineLabelPrefix: 'Pipeline:',
} as const

export const SHIKSHA_TEAM_CONTRIBUTION_UI = {
    titleIconName: 'people',
    titleIconSize: 'md',
} as const

export const SHIKSHA_SALES_FUNNEL_CONTENT = {
    ariaLabel: 'Shiksha sales funnel overview',
    title: 'Sales Funnel Overview',
    subtitle: 'Deals and value at each stage',
    legendWonLabel: 'Won / converted',
    legendActiveLabel: 'In progress',
    legendLostLabel: 'Lost / leaked',
    legendPoolLabel: 'Total pool (S0)',
    poolBarSuffixLabel: 'total deals entered pipeline',
    wonBarSuffixLabel: 'won',
    lostBarSuffixLabel: 'lost',
    stageHeader: 'Stage',
    progressHeader: 'Progress',
    conversionHeader: 'Conv.',
    tableStageHeaderFrom: 'From',
    tableStageHeaderTo: 'To',
    tableDealsHeader: 'Deals',
    tableWonHeader: 'Conversion Rate',
    tableInProgressHeader: 'In Progress',
    tableLostHeader: 'Lost',
    tableConvHeader: 'Conversion Rate',
    funnelViewLabel: 'Funnel view',
    tableViewLabel: 'Table view',
    funnelViewIconPath: 'assets/icons/bar-chart.svg',
    tableViewIconPath: 'assets/icons/grid-view.svg',
    viewModeAriaLabel: 'View mode',
    tableConversionCaption: 'success rate',
    /** Tooltip when stage code cannot be parsed (e.g. non S0–S5). */
    conversionTooltipFallback: 'to next stage',
    /** Placeholders: {current} and {next} expand to stage codes like S1, S2. */
    conversionTooltipTemplate: '{current} to {next} conversion',
    percentSymbol: '%',
    zeroPercentLabel: '0%',
    tableStageAriaJoiner: ' to ',
} as const

export const SHIKSHA_SALES_FUNNEL_UI = {
    titleIconName: 'trending_up',
    titleIconSize: 'md',
} as const

export const SHIKSHA_CLIENT_TYPE_REVENUE_CONTENT = {
    ariaLabel: 'Shiksha revenue by client type',
    title: 'Revenue by Client Type',
    subtitle: 'Client-wise revenue contribution',
} as const

export const SHIKSHA_CLIENT_TYPE_REVENUE_UI = {
    titleIconName: 'receipt',
    titleIconSize: 'md',
    shareTagAriaPrefix: 'Share of total',
    shareTagAriaSuffix: 'percent',
    trackAriaSuffix: 'percent of total',
    slimFillMaximumPercent: 3,
} as const

export const SHIKSHA_DEALS_AT_RISK_CONTENT = {
    ariaLabel: 'Shiksha deals at risk',
    title: 'Sales Leakage',
    subtitle: 'Stage-wise lost deal value, including aggregated lost value for Stage 4.',
    emptyTitle: 'No leakage data available',
    emptyMessage: 'There are no lost deals for the selected filters.',
} as const

export const SHIKSHA_DEALS_AT_RISK_UI = {
    titleIconName: 'warning',
    titleIconSize: 'md',
} as const

export const SHIKSHA_DASHBOARD_API_ENDPOINTS = {
    REVENUE_HEALTH_METRICS: '/api/aggregated/v1/targets/revenue-health-metrics',
    S3_ESTIMATE_DEAL_VALUE: '/api/lead-management/v1/targets/deals/s3-estimate-value',
    LOST_S3_S4_AGGREGATION: '/api/aggregated/v1/targets/deals/lost-s3-s4-aggregation',
    SALES_FUNNEL_SNAPSHOTS: '/api/lead-management/v1/targets/sales-funnel-snapshots',
    FINANCIAL_YEARS: '/api/lead-management/v1/targets/financial-years',
    CUSTOMER_ESTIMATES_BY_FINANCIAL_YEAR:
        '/api/aggregated/v1/shiksha/users/deals/customer-estimates',
    PIPELINE_RISK: '/api/aggregated/v1/shiksha/users/deals/pipeline-risk',
} as const

export const SHIKSHA_DASHBOARD_TEXT = {
    currencyCodeInr: 'INR',
    currencySymbolRupee: '₹',
    zeroPercentOfTarget: '0% of target',
    percentOfTargetSuffix: '% of target',
    memberFallbackPrefix: 'Member',
    memberFallbackSuffix: 'User',
} as const

export const SHIKSHA_CLIENT_TYPE_REVENUE_FORMAT = {
    croreThreshold: 10000000,
    lakhThreshold: 100000,
    croreDivisor: 10000000,
    lakhDivisor: 100000,
    croreUnit: 'Cr',
    lakhUnit: 'L',
    localeIndianEnglish: 'en-IN',
} as const

export const SHIKSHA_DASHBOARD_API_PARAMS = {
    FINANCIAL_YEAR: 'financial_year',
    SCOPE: 'scope',
    TEAM_CONTRIBUTION: 'team_contribution',
    TEAM_VIEW: 'team_view',
    USER_ID: 'user_id',
    USER_IDS: 'user_ids',
} as const

export const SHIKSHA_DASHBOARD_KPI_LABELS = {
    DEAL_WON_VALUE: 'Received',
    PIPELINE_TARGET: 'Target',
    GAP_TO_TARGET: 'Gap',
    PIPELINE_COVERAGE: 'Coverage',
    PIPELINE_COVERAGE_SUBLABEL: 'Received / Target',
    TEAM_CONVERSION_PREFIX: 'Conv',
    TEAM_PIPELINE_PREFIX: 'Pipeline',
} as const

export const SHIKSHA_DASHBOARD_TOOLTIP_TEXT = {
    targetPipeline: 'Total target set for the team in this financial year',
    receivedRevenue: 'Total signed order value achieved for the selected year and scope.',
    coverageRatio: 'Deal won value divided by pipeline target ',
    gapToTarget: 'The remaining order amount required to achieve the selected pipeline target.',
} as const

export const SHIKSHA_DASHBOARD_STATE_TEXT = {
    loadingAriaLabel: 'Loading shiksha dashboard',
    noAccessTitle: 'No access to Shiksha dashboard',
    noAccessMessage:
        'You do not have permission to view this dashboard. Please contact your admin.',
} as const

// Funnel snapshot API uses compressed stage codes S0-S5 (different from national S0-S9).
export const SHIKSHA_FUNNEL_DEAL_WON_STAGE_CODE = 'S5' as const

/** When the API omits a readable stage name, e.g. `Stage 3`. Placeholder: {n} = 1-based index. */
export const SHIKSHA_FUNNEL_STAGE_FALLBACK = {
    numberedStageTemplate: 'Stage {n}',
} as const

export function buildShikshaNumberedStageFallbackLabel(zeroBasedIndex: number): string {
    return SHIKSHA_FUNNEL_STAGE_FALLBACK.numberedStageTemplate.replace(
        '{n}',
        String(zeroBasedIndex + 1),
    )
}

/** Canonical UI labels by funnel stage code (used when API sends generic names like "Stage 1"). */
export const SHIKSHA_FUNNEL_STAGE_LABEL_BY_CODE: Readonly<Record<string, string>> = {
    S0: 'All Deals',
    S1: 'Deal Qualified',
    S2: 'Proposal Stage',
    S3: 'Engagement Stage',
    S4: 'Deal Confirmed',
    S5: 'Deal Won',
}

/** Table view “From -> To” rows in order; metrics use the destination (`toCode`) stage. */
export const SHIKSHA_SALES_FUNNEL_TABLE_STAGE_TRANSITIONS: readonly {
    readonly fromCode: string
    readonly toCode: string
}[] = [
    { fromCode: 'S0', toCode: 'S1' },
    { fromCode: 'S1', toCode: 'S2' },
    { fromCode: 'S2', toCode: 'S3' },
    { fromCode: 'S3', toCode: 'S4' },
    { fromCode: 'S4', toCode: 'S5' },
]

export const SHIKSHA_DASHBOARD_CURRENCY = {
    INR_PREFIX: '₹',
    INR_ZERO: '₹0',
    INR_COMPACT_SYMBOL: '₹',
    CRORE_VALUE: 10000000,
    LAKH_VALUE: 100000,
    CRORE_SUFFIX: 'Cr',
    LAKH_SUFFIX: 'L',
} as const

export const SHIKSHA_DROPDOWN_CONTENT = {
    scopeLabel: 'View Scope:',
    yearLabel: 'Year:',
    scopePlaceholder: 'Select scope',
    yearPlaceholder: 'Select year',
    refreshAriaLabel: 'Refresh Shiksha dashboard',
} as const

export const SHIKSHA_PIPELINE_RISK_STAGE_KEYS = {
    STAGE_3: 'stage_3',
    STAGE_4: 'stage_4',
} as const

export const SHIKSHA_PIPELINE_RISK_RISK_KEYS = {
    HIGH: 'high',
    MODERATE: 'moderate',
} as const

export const SHIKSHA_PIPELINE_RISK_STAGE_ORDER = [
    SHIKSHA_PIPELINE_RISK_STAGE_KEYS.STAGE_3,
    SHIKSHA_PIPELINE_RISK_STAGE_KEYS.STAGE_4,
] as const

export const SHIKSHA_PIPELINE_RISK_STAGE_LABELS: Readonly<Record<string, string>> = {
    [SHIKSHA_PIPELINE_RISK_STAGE_KEYS.STAGE_3]: 'Engagement Stage',
    [SHIKSHA_PIPELINE_RISK_STAGE_KEYS.STAGE_4]: 'Deal Confirmed',
}

export const SHIKSHA_PIPELINE_RISK_STAGE_DISPLAY_CODES: Readonly<Record<string, string>> = {
    [SHIKSHA_PIPELINE_RISK_STAGE_KEYS.STAGE_3]: 'S3',
    [SHIKSHA_PIPELINE_RISK_STAGE_KEYS.STAGE_4]: 'S4',
}

export const SHIKSHA_PIPELINE_RISK_DEFAULT_STUCK_DAYS: Readonly<Record<string, number>> = {
    [SHIKSHA_PIPELINE_RISK_STAGE_KEYS.STAGE_3]: 30,
    [SHIKSHA_PIPELINE_RISK_STAGE_KEYS.STAGE_4]: 45,
}

export const SHIKSHA_PIPELINE_RISK_CONTENT = {
    ariaLabel: 'Shiksha pipeline risk by stage',
    title: 'Deals At Risk',
    subtitle: 'Deals tracked with no recent activities in the given stages',
    highRiskLabel: 'High Risk',
    leadSuffix: 'Leads',
    atRiskSuffix: 'At Risk',
    stageSummaryTitleTemplate: 'Deals in {stage} stuck for over {days} days',
    dealLabelSingular: 'Deal',
    dealLabelPlural: 'Deals',
    /** Placeholders: {stageLabel}, {highRiskLabel}, {count}, {dealLabel}, {amountSuffix}. */
    stageRiskAriaTemplate: '{stageLabel}, {highRiskLabel}, {count} {dealLabel}{amountSuffix}',
    /** Placeholders: {amount}, {atRiskSuffix}. */
    stageRiskAriaAmountSuffix: ', {amount} {atRiskSuffix}',
} as const

export const SHIKSHA_PIPELINE_RISK_UI = {
    titleIconName: 'warning',
    titleIconSize: 'md',
    highLabel: 'High Risk',
    moderateLabel: 'Moderate',
} as const

export const SHIKSHA_PIPELINE_RISK_RUPEE_COMPACT_FORMAT = {
    rupeeSymbol: SHIKSHA_DASHBOARD_TEXT.currencySymbolRupee,
    croreThreshold: SHIKSHA_CLIENT_TYPE_REVENUE_FORMAT.croreThreshold,
    lakhThreshold: SHIKSHA_CLIENT_TYPE_REVENUE_FORMAT.lakhThreshold,
    croreDivisor: SHIKSHA_CLIENT_TYPE_REVENUE_FORMAT.croreDivisor,
    lakhDivisor: SHIKSHA_CLIENT_TYPE_REVENUE_FORMAT.lakhDivisor,
    locale: SHIKSHA_CLIENT_TYPE_REVENUE_FORMAT.localeIndianEnglish,
} as const

/** Legacy aliases without `I` prefix — use `IShiksha*` from `@features/home/models/shiksha-dashboard.model` in new code. */
export type {
    IShikshaClientTypeRevenue as ShikshaClientTypeRevenue,
    IShikshaKpiItem as ShikshaKpiItem,
    IShikshaSalesFunnelStage as ShikshaSalesFunnelStage,
    IShikshaTeamContributionMember as ShikshaTeamContributionMember,
} from '@features/home/models/shiksha-dashboard.model'
